'use client';

import { useState } from 'react';
import Image from 'next/image';
import { Star, Users, Coffee, ChevronLeft, ChevronRight, Camera } from 'lucide-react';
import { AmenityIcon } from '@/components/AmenityIcon';

export default function HeroCarouselV2({
  images,
  roomName,
  category,
  price,
  maxGuests,
  hasBreakfast,
  rating,
  reviewCount,
  featureLabels = [],
}: {
  images: string[];
  roomName: string;
  category?: string;
  price?: number;
  maxGuests?: number;
  hasBreakfast?: boolean;
  rating?: number | null;
  reviewCount?: number;
  featureLabels?: string[];
}) {
  const [current, setCurrent] = useState(0);
  const total = images.length;

  function prev() { setCurrent(i => (i - 1 + total) % total); }
  function next() { setCurrent(i => (i + 1) % total); }

  const ratingLabel =
    rating == null  ? null :
    rating >= 4.8   ? 'Exceptional' :
    rating >= 4.5   ? 'Excellent' :
    rating >= 4.0   ? 'Very Good' :
    rating >= 3.5   ? 'Good' : null;

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', borderRadius:'1.6rem' }}>
      {/* ── Images ── */}
      {images.map((src, i) => (
        <div key={i} style={{
          position: 'absolute', inset: 0,
          opacity: i === current ? 1 : 0,
          transition: 'opacity 0.5s ease',
        }}>
          <Image
            src={src} alt={i === 0 ? roomName : ''} fill priority={i === 0}
            sizes="100vw" className="object-cover" style={{ objectPosition: 'center 40%' }}
          />
        </div>
      ))}

      {/* ── Gradients ── */}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.42) 55%, transparent 100%)' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 45%)' }} />

      {/* ── Top-left: room info overlay ── */}
      <div style={{
        position: 'absolute', top: 0, left: 0,
        padding: 'clamp(1.8rem,3vw,3.2rem) clamp(1.6rem,5vw,4.5rem)',
        maxWidth: 'min(72%, 660px)',
      }}>
        {/* Category badge */}
        {category && (
          <span style={{
            display: 'inline-block',
            background: 'rgba(163,146,105,0.92)', color: '#fff',
            fontFamily: 'var(--font-sans,sans-serif)', fontSize: '1rem', fontWeight: 700,
            letterSpacing: '0.16em', textTransform: 'uppercase',
            padding: '0.4rem 1.1rem', borderRadius: '0.4rem',
            marginBottom: '1.2rem',
          }}>
            {category}
          </span>
        )}

        {/* Room name */}
        <h1 style={{
          fontFamily: 'var(--font-serif,serif)',
          fontSize: 'clamp(2.8rem,5vw,5.2rem)',
          fontWeight: 400, letterSpacing: '-0.04em', lineHeight: 0.95,
          color: '#fff', margin: '0 0 1.2rem',
        }}>
          {roomName}
        </h1>

        {/* Rating row */}
        {rating != null && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: 'var(--font-sans,sans-serif)', fontSize: '1.3rem', color: 'rgba(255,255,255,0.9)' }}>
              <Star size={14} fill="#d7a65c" color="#d7a65c" strokeWidth={1} />
              {rating.toFixed(1)}{reviewCount ? ` (${reviewCount} reviews)` : ''}
            </span>
            {ratingLabel && <span style={{ fontFamily: 'var(--font-sans,sans-serif)', fontSize: '1.3rem', fontWeight: 500, color: '#4ade80' }}>{ratingLabel}</span>}
          </div>
        )}

        {/* Price */}
        {price != null && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.9rem' }}>
            <span
              
              style={{
                fontFamily: 'var(--font-serif,serif)',
                fontSize: 'clamp(2.6rem,4.5vw,4rem)',
                fontWeight: 400, letterSpacing: '-0.05em', color: '#fff',
              }}
            >
              ₹{price.toLocaleString('en-IN')}
            </span>
            <span style={{ fontFamily: 'var(--font-sans,sans-serif)', fontSize: '1.4rem', color: 'rgba(255,255,255,0.6)' }}>/ night</span>
          </div>
        )}

        {/* Includes */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.6rem', flexWrap: 'wrap' }}>
          {maxGuests && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: 'var(--font-sans,sans-serif)', fontSize: '1.2rem', color: 'rgba(255,255,255,0.8)' }}>
              <Users size={14} strokeWidth={1.2} />
              {maxGuests} guest{maxGuests !== 1 ? 's' : ''} included
            </span>
          )}
          {hasBreakfast && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: 'var(--font-sans,sans-serif)', fontSize: '1.2rem', color: 'rgba(255,255,255,0.8)' }}>
              <Coffee size={14} strokeWidth={1.5} />
              Includes breakfast
            </span>
          )}
        </div>
      </div>

      {/* ── Bottom-left: feature strip ── */}
      {featureLabels.length > 0 && (
        <div className="hero-feature-strip" style={{
          position: 'absolute',
          bottom: 'clamp(1.6rem,2.5vw,2.8rem)',
          left: 'clamp(1.6rem,5vw,4.5rem)',
          display: 'flex', gap: 'clamp(1.4rem,2.5vw,3rem)', alignItems: 'flex-end',
        }}>
          {featureLabels.slice(0, 6).map(label => (
            <div key={label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.45rem', textAlign: 'center' }}>
              <span style={{ color: 'rgba(255,255,255,0.85)', lineHeight: 0 }}>
                <AmenityIcon label={label} size={18} />
              </span>
              <span className="hero-feature-label" style={{ fontFamily: 'var(--font-sans,sans-serif)', fontSize: '1rem', color: 'rgba(255,255,255,0.8)', whiteSpace: 'nowrap' }}>
                {label}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── Prev / Next ── */}
      {total > 1 && (
        <>
          <button type="button" onClick={prev} aria-label="Previous" style={{
            position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)',
            width: '3.2rem', height: '3.2rem', borderRadius: '50%',
            background: 'rgba(255,255,255,0.85)', border: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}>
            <ChevronLeft size={14} color="#0D2F44" strokeWidth={1.6} />
          </button>
          <button type="button" onClick={next} aria-label="Next" style={{
            position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)',
            width: '3.2rem', height: '3.2rem', borderRadius: '50%',
            background: 'rgba(255,255,255,0.85)', border: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}>
            <ChevronRight size={14} color="#0D2F44" strokeWidth={1.6} />
          </button>
        </>
      )}

      {/* ── Photo counter (bottom-right) ── */}
      <div style={{
        position: 'absolute', bottom: '1.4rem', right: '1.4rem',
        display: 'flex', alignItems: 'center', gap: '0.4rem',
        background: 'rgba(0,0,0,0.5)', color: '#fff', borderRadius: '0.4rem',
        padding: '0.35rem 0.8rem',
        fontFamily: 'var(--font-sans,sans-serif)', fontSize: '1.05rem',
      }}>
        <Camera size={13} color="#fff" strokeWidth={1.3} />
        {current + 1} / {total}
      </div>
    </div>
  );
}
